/**
 * AI Inline Menu
 * Menu flutuante ativado pelo comando "/"
 */

import React, { useEffect, useRef, useState } from 'react';
import { isFeatureEnabled } from '@/config/features';
import { AI_COMMANDS } from '@/lib/ai/types';
import { useAIInline } from './AIInlineProvider';

interface AIInlineMenuProps {
  position: { x: number; y: number };
  visible: boolean;
  onSelectCommand: (command: any) => void;
  onClose: () => void;
}

export const AIInlineMenu: React.FC<AIInlineMenuProps> = ({
  position,
  visible,
  onSelectCommand,
  onClose,
}) => {
  // Feature flag check
  if (!isFeatureEnabled('ai_inline_editor')) {
    return null;
  }

  const menuRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { selectedText } = useAIInline();

  // Filtrar comandos disponíveis baseado na seleção
  const availableCommands = AI_COMMANDS.filter(command => 
    !command.requiresSelection || selectedText.length > 0
  );

  // Manipular teclas
  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < availableCommands.length - 1 ? prev + 1 : 0
          );
          break;

        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : availableCommands.length - 1
          );
          break;

        case 'Enter':
          e.preventDefault();
          if (availableCommands[selectedIndex]) {
            onSelectCommand(availableCommands[selectedIndex]);
          }
          break;

        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, selectedIndex, availableCommands, onSelectCommand, onClose]);

  // Fechar ao clicar fora
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [visible, onClose]);

  // Reset index quando menu abre
  useEffect(() => {
    if (visible) {
      setSelectedIndex(0);
    }
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      ref={menuRef}
      className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-w-xs min-w-[250px]"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translateY(-100%)',
      }}
    >
      <div className="p-2">
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 px-2">
          Comandos de IA {selectedText ? '(texto selecionado)' : ''}
        </div>
        
        {availableCommands.map((command, index) => (
          <button
            key={command.id}
            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
              index === selectedIndex
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
            } ${
              command.requiresSelection && !selectedText
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
            onClick={() => {
              if (!command.requiresSelection || selectedText) {
                onSelectCommand(command);
              }
            }}
            disabled={command.requiresSelection && !selectedText}
          >
            <div className="flex items-center gap-2">
              <span className="text-base">{command.icon}</span>
              <div>
                <div className="font-medium">{command.label}</div>
                {command.description && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {command.description}
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
        
        {availableCommands.length === 0 && (
          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
            Nenhum comando disponível
          </div>
        )}
      </div>
      
      <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2">
        <div className="text-xs text-gray-400 dark:text-gray-500">
          ↑↓ navegar • Enter selecionar • Esc fechar
        </div>
      </div>
    </div>
  );
};

export default AIInlineMenu;